<script setup lang="ts">
/**
 * The only modal shell. 34px header with the entity name, body in card language,
 * footer with every button pressed to the right at one minimum width: the way out (outline),
 * then the one action as the rightmost button, drawn without fill (green ink, red when
 * irreversible) — founder 2026-09-10.
 */
import Icon from '~/components/ui/Icon.vue'
import type { Ref } from 'vue'
import { formatLiveDuration } from '~/utils/trainingProgress'
const status = inject<{ pending: Ref<boolean>; error: Ref<string>; busy?: Ref<string>; busySince?: Ref<number> } | null>('studio-dialog-status', null)
/* While the press is being carried out the dialog says what is being done and how long it has
   been doing it, counted in seconds. A still, greyed-out dialog reads as a fault, and on a large
   file the wait is long enough to matter (founder, 2026-09-21, note 1). */
const now = useNow()
const busyLine = computed(() => {
  if (!status?.pending.value || !status.busy) return ''
  const since = status.busySince?.value ?? 0
  const seconds = since > 0 ? Math.max(0, Math.floor((now.value - since) / 1000)) : 0
  return `${status.busy.value}… ${formatLiveDuration(seconds)}`
})

withDefaults(defineProps<{ title: string; meta?: string; width?: number }>(), { width: 520, meta: undefined })
const emit = defineEmits<{ close: [] }>()
const content = ref<HTMLElement | null>(null)
let previous: HTMLElement | null = null
function onKey(event: KeyboardEvent) {
  if(event.key === 'Escape' && !status?.pending.value) { event.preventDefault(); emit('close'); return }
  if(event.key !== 'Tab' || !content.value) return
  const nodes = [...content.value.querySelectorAll<HTMLElement>('button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),a[href],[tabindex="0"]')].filter(node => node.getClientRects().length)
  const first=nodes[0], last=nodes.at(-1)
  if(!first) { event.preventDefault(); content.value.focus(); return }
  if(event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
  else if(!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
}
onMounted(() => { previous=document.activeElement instanceof HTMLElement ? document.activeElement : null; (content.value?.querySelector<HTMLElement>('input,textarea') ?? content.value?.querySelector<HTMLElement>('button'))?.focus(); document.addEventListener('keydown',onKey) })
onBeforeUnmount(() => { document.removeEventListener('keydown',onKey); previous?.focus() })
</script>

<template>
  <Teleport to="body">
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-background/70 p-[var(--dialog-viewport-inset)] backdrop-blur-sm">
    <section
ref="content" tabindex="-1"
role="dialog" aria-modal="true" :aria-label="title" class="flex max-h-full min-w-0 max-w-full flex-col overflow-hidden rounded-lg border border-border bg-card shadow-elevated"
             :style="{ width: `var(--dialog-width-${width})` }">
      <header class="flex h-[var(--card-header-height)] shrink-0 items-center gap-2.5 border-b border-border px-3">
        <span class="flex min-w-0 max-w-[var(--dialog-title-max-width)] shrink-0 items-center gap-1.5 text-xs font-semibold [&_svg]:size-3 [&_svg]:shrink-0">
          <slot name="icon" /><span class="truncate">{{ title }}</span>
        </span>
        <span v-if="meta" class="num min-w-0 truncate text-[length:var(--type-meta)] text-muted-foreground">{{ meta }}</span>
        <span class="flex-1" />
        <!-- One header control or link action, as a card header carries: a second view of the same
             dialog is reached from here without adding a row to the body. -->
        <slot name="aside" />
        <button type="button" aria-label="Close dialog" :disabled="status?.pending.value" class="hit shrink-0 text-muted-foreground hover:text-foreground" @click="$emit('close')">
          <Icon name="x" :size="12" />
        </button>
      </header>

      <div class="flex min-h-0 flex-col gap-3 overflow-auto p-3.5">
        <p v-if="status?.error.value" role="alert" class="text-xs text-destructive">{{ status.error.value }}</p>
        <p v-if="busyLine" role="status" class="num m-0 text-[length:var(--type-meta)] text-muted-foreground">{{ busyLine }}</p>
        <fieldset :disabled="status?.pending.value" class="contents"><slot /></fieldset>
      </div>

      <footer v-if="$slots.actions" class="flex shrink-0 items-center justify-end gap-2.5 border-t border-border p-3 [&_button]:min-w-[var(--dialog-button-min-width)]">
        <slot name="actions" />
      </footer>
    </section>
  </div>
  </Teleport>
</template>
